import {
  maskCpfOrCnpj,
  maskMoney,
  maskPhoneNumber,
  maskZipCode
} from '../../../services/masks';

import { ActivationFormState } from '../types';
import { getActivationValue, toInputBirthdate } from './activationFormHelpers';

type ActivationPayloadSource = {
  recipientType?: ActivationFormState['recipientType'];
  registerInformation?: Record<string, unknown>;
  defaultBankAccount?: Record<string, unknown>;
  payload?: {
    registerInformation?: Record<string, unknown>;
    register_information?: Record<string, unknown>;
    defaultBankAccount?: Record<string, unknown>;
    default_bank_account?: Record<string, unknown>;
  };
};

const readRegisterInformation = (source: ActivationPayloadSource): Record<string, unknown> => {
  const payload = source.payload || {};
  return (
    source.registerInformation ||
    payload.registerInformation ||
    payload.register_information ||
    {}
  );
};

const readBankAccount = (source: ActivationPayloadSource): Record<string, unknown> => {
  const payload = source.payload || {};
  return (
    source.defaultBankAccount ||
    payload.defaultBankAccount ||
    payload.default_bank_account ||
    {}
  );
};

const readPhoneFromPagarme = (registerInformation: Record<string, unknown>): string => {
  const phones = registerInformation.phone_numbers;
  if (!Array.isArray(phones) || !phones.length) return '';
  const phone = phones[0] as { ddd?: number | string; number?: number | string };
  const ddd = String(phone?.ddd || '');
  const number = String(phone?.number || '');
  if (!ddd && !number) return '';
  return maskPhoneNumber(`${ddd}${number}`);
};

const readMoneyFromPagarme = (
  source: Record<string, unknown> | undefined,
  field: string,
  fallback: string
): string => {
  const value = getActivationValue(source, field);
  return value ? maskMoney(value, true) : fallback;
};

export const mapActivationPayloadToForm = <T extends ActivationFormState>(
  source: ActivationPayloadSource,
  base: T
): T => {
  const registerInformation = readRegisterInformation(source);
  const defaultBankAccount = readBankAccount(source);
  const recipientType =
    source.recipientType ||
    (registerInformation.type as ActivationFormState['recipientType']) ||
    base.recipientType;

  const isCorporation = recipientType === 'corporation';
  const address = (isCorporation
    ? registerInformation.main_address
    : registerInformation.address) as Record<string, unknown> | undefined;

  const partner = (
    Array.isArray(registerInformation.managing_partners)
      ? registerInformation.managing_partners[0]
      : null
  ) as Record<string, unknown> | undefined;

  const partnerAddress = (partner?.address || {}) as Record<string, unknown>;
  const documentRaw = getActivationValue(registerInformation, 'document');
  const holderDocumentRaw = getActivationValue(defaultBankAccount, 'holder_document');

  return {
    ...base,
    recipientType,
    email: getActivationValue(registerInformation, 'email', base.email),
    document: documentRaw ? maskCpfOrCnpj(documentRaw) : base.document,
    phone: readPhoneFromPagarme(registerInformation) || base.phone,
    name: getActivationValue(registerInformation, 'name', base.name),
    motherName: getActivationValue(registerInformation, 'mother_name', base.motherName),
    birthdate: toInputBirthdate(getActivationValue(registerInformation, 'birthdate', base.birthdate)),
    monthlyIncome: readMoneyFromPagarme(
      registerInformation,
      'monthly_income',
      base.monthlyIncome
    ),
    professionalOccupation: getActivationValue(
      registerInformation,
      'professional_occupation',
      base.professionalOccupation
    ),
    companyName: getActivationValue(registerInformation, 'company_name', base.companyName),
    tradingName: getActivationValue(registerInformation, 'trading_name', base.tradingName),
    annualRevenue: readMoneyFromPagarme(
      registerInformation,
      'annual_revenue',
      base.annualRevenue
    ),
    street: getActivationValue(address, 'street', base.street),
    complementary: getActivationValue(address, 'complementary', base.complementary),
    streetNumber: getActivationValue(address, 'street_number', base.streetNumber),
    neighborhood: getActivationValue(address, 'neighborhood', base.neighborhood),
    city: getActivationValue(address, 'city', base.city),
    state: getActivationValue(address, 'state', base.state).toUpperCase(),
    zipCode: getActivationValue(address, 'zip_code')
      ? maskZipCode(getActivationValue(address, 'zip_code'))
      : base.zipCode,
    referencePoint: getActivationValue(address, 'reference_point', base.referencePoint),
    partnerName: getActivationValue(partner, 'name', base.partnerName),
    partnerEmail: getActivationValue(partner, 'email', base.partnerEmail),
    partnerDocument: getActivationValue(partner, 'document')
      ? maskCpfOrCnpj(getActivationValue(partner, 'document'))
      : base.partnerDocument,
    partnerPhone: partner ? readPhoneFromPagarme(partner) : base.partnerPhone,
    partnerMotherName: getActivationValue(partner, 'mother_name', base.partnerMotherName),
    partnerBirthdate: toInputBirthdate(
      getActivationValue(partner, 'birthdate', base.partnerBirthdate)
    ),
    partnerMonthlyIncome: readMoneyFromPagarme(
      partner,
      'monthly_income',
      base.partnerMonthlyIncome
    ),
    partnerProfessionalOccupation: getActivationValue(
      partner,
      'professional_occupation',
      base.partnerProfessionalOccupation
    ),
    partnerStreet: getActivationValue(partnerAddress, 'street', base.partnerStreet),
    partnerComplementary: getActivationValue(
      partnerAddress,
      'complementary',
      base.partnerComplementary
    ),
    partnerStreetNumber: getActivationValue(
      partnerAddress,
      'street_number',
      base.partnerStreetNumber
    ),
    partnerNeighborhood: getActivationValue(
      partnerAddress,
      'neighborhood',
      base.partnerNeighborhood
    ),
    partnerCity: getActivationValue(partnerAddress, 'city', base.partnerCity),
    partnerState: getActivationValue(partnerAddress, 'state', base.partnerState).toUpperCase(),
    partnerZipCode: getActivationValue(partnerAddress, 'zip_code')
      ? maskZipCode(getActivationValue(partnerAddress, 'zip_code'))
      : base.partnerZipCode,
    partnerReferencePoint: getActivationValue(
      partnerAddress,
      'reference_point',
      base.partnerReferencePoint
    ),
    bankHolderName: getActivationValue(defaultBankAccount, 'holder_name', base.bankHolderName),
    bankHolderType:
      getActivationValue(defaultBankAccount, 'holder_type') === 'company'
        ? 'company'
        : getActivationValue(defaultBankAccount, 'holder_type') === 'individual'
          ? 'individual'
          : isCorporation
            ? 'company'
            : base.bankHolderType,
    bankHolderDocument: holderDocumentRaw
      ? maskCpfOrCnpj(holderDocumentRaw)
      : isCorporation
        ? maskCpfOrCnpj(documentRaw)
        : base.bankHolderDocument,
    bankCode: getActivationValue(defaultBankAccount, 'bank', base.bankCode),
    bankBranchNumber: getActivationValue(
      defaultBankAccount,
      'branch_number',
      base.bankBranchNumber
    ),
    bankBranchCheckDigit: getActivationValue(
      defaultBankAccount,
      'branch_check_digit',
      base.bankBranchCheckDigit
    ),
    bankAccountNumber: getActivationValue(
      defaultBankAccount,
      'account_number',
      base.bankAccountNumber
    ),
    bankAccountCheckDigit: getActivationValue(
      defaultBankAccount,
      'account_check_digit',
      base.bankAccountCheckDigit
    ),
    bankAccountType:
      getActivationValue(defaultBankAccount, 'type') === 'savings' ? 'savings' : 'checking'
  } as T;
};
